import { useCallback, useEffect, useRef, useState } from 'react';
import { vapidPublicKey } from 'config/vapid';
import PushSubscriptionsService from 'services/push-subscriptions.service';

const service = new PushSubscriptionsService();

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

interface UseWebPushOptions {
  carrierRootId?: number | null;
  isAdmin?: boolean;
}

export const useWebPush = ({ carrierRootId, isAdmin = false }: UseWebPushOptions) => {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const registered = useRef(false);
  const canRegister = Boolean(carrierRootId) || isAdmin;

  const register = useCallback(async () => {
    if (!canRegister || registered.current) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !vapidPublicKey) return;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') return;

      await navigator.serviceWorker.register('/push-sw.js');
      const sw = await navigator.serviceWorker.ready;
      const existing = await sw.pushManager.getSubscription();
      if (existing) {
        await existing.unsubscribe();
      }
      const subscription = await sw.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      const { endpoint } = subscription;
      const p256dhBuffer = subscription.getKey('p256dh');
      const authBuffer = subscription.getKey('auth');
      if (!p256dhBuffer || !authBuffer) return;

      const p256dh = btoa(String.fromCharCode(...Array.from(new Uint8Array(p256dhBuffer))));
      const auth = btoa(String.fromCharCode(...Array.from(new Uint8Array(authBuffer))));

      await service.upsert(endpoint, p256dh, auth, carrierRootId);
      registered.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('WebPush registration failed', error);
    }
  }, [canRegister, carrierRootId, isAdmin]);

  const unregister = useCallback(async () => {
    if (!canRegister) return;
    try {
      const swReg = await navigator.serviceWorker.getRegistration('/push-sw.js');
      if (!swReg) return;
      const subscription = await swReg.pushManager.getSubscription();
      if (!subscription) return;
      const { endpoint } = subscription;
      await subscription.unsubscribe();
      await service.remove(endpoint, carrierRootId);
      registered.current = false;
    } catch {
      // silently ignore
    }
  }, [canRegister, carrierRootId]);

  useEffect(() => {
    if (canRegister && permission !== 'denied' && !registered.current) {
      register();
    }
  }, [canRegister, permission, register]);

  return { permission, register, unregister };
};
